import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "@tamagui/lucide-icons";
import { Icon } from "../Icon";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { XStack } from "../Stacks";
import { Caption, Subtitle1 } from "../Typography";
const NAV_BUTTON_SIZE = "$si5.5";
type NavButtonProps = {
  label: string;
  onPress: () => void;
  direction: "previous" | "next";
};
const NavButton = ({
  label,
  onPress,
  direction
}: NavButtonProps) => {
  const {
    interactiveProps
  } = useInteractiveElement({
    kind: "button",
    onPress
  });
  return <XStack role="button" aria-label={label} alignItems="center" justifyContent="center" width={NAV_BUTTON_SIZE} height={NAV_BUTTON_SIZE} borderRadius="$brFull" cursor="pointer" hoverStyle={{
    backgroundColor: "$surface2Hover"
  }} pressStyle={{
    backgroundColor: "$surface0Hover"
  }} focusVisibleStyle={{
    outlineWidth: "$border3",
    outlineColor: "$borderIntense",
    outlineStyle: "solid"
  }} {...interactiveProps} data-sentry-element="XStack" data-sentry-component="NavButton" data-sentry-source-file="dateParts.tsx">
      <Icon icon={direction === "previous" ? ChevronLeftIcon : ChevronRightIcon} size="$iconMedium" color="$iconDefault" data-sentry-element="Icon" data-sentry-source-file="dateParts.tsx" />
    </XStack>;
};
export type CalendarHeaderProps = {
  /** Month + year already localized by rehookify (e.g. "June" / "2026"). */
  monthLabel: string;
  yearLabel: string;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  previousLabel?: string;
  nextLabel?: string;
};

/** Month-year title (`Subtitle1`) flanked by prev/next chevron nav. */
export const CalendarHeader = ({
  monthLabel,
  yearLabel,
  onPreviousMonth,
  onNextMonth,
  previousLabel = "Previous month",
  nextLabel = "Next month"
}: CalendarHeaderProps) => <XStack alignItems="center" justifyContent="space-between" width="100%" paddingHorizontal="$sp0.5" data-sentry-element="XStack" data-sentry-component="CalendarHeader" data-sentry-source-file="dateParts.tsx">
    <NavButton label={previousLabel} onPress={onPreviousMonth} direction="previous" data-sentry-element="NavButton" data-sentry-source-file="dateParts.tsx" />
    <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="dateParts.tsx">{`${monthLabel} ${yearLabel}`}</Subtitle1>
    <NavButton label={nextLabel} onPress={onNextMonth} direction="next" data-sentry-element="NavButton" data-sentry-source-file="dateParts.tsx" />
  </XStack>;
export type WeekViewProps = {
  /** rehookify's localized weekday labels, ordered by `startDay`. */
  weekDays: string[];
};

/** Su–Sa (or Mo–Su) weekday header row. */
export const WeekView = ({
  weekDays
}: WeekViewProps) => <XStack width="100%" aria-hidden data-sentry-element="XStack" data-sentry-component="WeekView" data-sentry-source-file="dateParts.tsx">
    {weekDays.map((label, index) => <Caption
  // Weekday labels can repeat first letters; index keys the fixed 7-slot row.
  key={`${label}-${index}`} flex={1} flexBasis={0} textAlign="center" color="$textMuted">
        {label}
      </Caption>)}
  </XStack>;